import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import type { PlainOrganizationMember } from "@/queries/organization-queries";
import { useProjectPrebuilds } from "@/queries/prebuild-queries";
import { useDeleteProject, type PlainProject, type ProjectState } from "@/queries/project-queries";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";

type DeleteProjectModalProps = {
    project: PlainProject;
    state: ProjectState;
    creator: PlainOrganizationMember | undefined;
    currentMember: PlainOrganizationMember | undefined;
    onClose: () => void;
};

export const DeleteProjectModal: FC<DeleteProjectModalProps> = ({
    project,
    onClose,
    state,
}: DeleteProjectModalProps) => {
    const { toast } = useToast();

    const deleteProject = useDeleteProject();
    const listPrebuilds = useProjectPrebuilds(project.id);

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteProject = useCallback(() => {
        deleteProject.mutate(
            {
                projectId: project.id,
            },
            {
                onSuccess: () => {
                    toast({ title: "Project deleted" });
                    onClose();
                },
                onError: (err) => {
                    toast({ title: "Failed to delete project", description: formatError(err) });
                },
            },
        );
    }, [deleteProject, onClose, project, toast]);

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-track-location={TrackLocations.ProjectDeleteModal}>
                <DialogHeader>
                    <DialogTitle>Delete project</DialogTitle>
                    <DialogDescription>
                        {(listPrebuilds.data?.prebuilds.length ?? 0) > 0 ? (
                            <>
                                <Text>All prebuilds associated with this project will also be deleted.</Text>
                                <Text className="font-bold">
                                    This will affect {listPrebuilds.data?.prebuilds.length} prebuild
                                    <span>{(listPrebuilds.data?.prebuilds.length ?? 0) > 1 ? "s" : ""}</span>.
                                </Text>
                            </>
                        ) : (
                            <Text>There are no prebuilds associated with this project.</Text>
                        )}
                        <Text>Are you sure you&apos;d like to delete it?</Text>
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <div className="group flex flex-col justify-between gap-4 overflow-x-hidden rounded-xl border border-border-base bg-surface-glass px-4 py-2.5 pl-3">
                        <div className="flex flex-col">
                            <Text className="text-base font-bold">{project.metadata?.name}</Text>
                            <Text className="text-sm text-content-secondary">
                                {state.shared && <span>Shared · used by {project.usedBy?.totalSubjects} members</span>}
                                {!state.shared && <span>Not shared</span>}
                            </Text>
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            data-tracking-id="cancel-delete-project-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deleteProject.isPending}
                        onClick={handleDeleteProject}
                        data-tracking-id="confirm-delete-project-modal"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
